import { updateTask } from '@commenda/api';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { documentName, getPrintableDirectorName } from '@commenda/utils';
import {
  IncorporationRequestStatus,
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ConfirmationModal } from '../../../../molecules/modals/ConfirmationModal';
import { useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { WorkflowProcessDrawer } from '../../../common/workflow-process-drawer';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import {
  areDocumentsPresent,
  isDocumentPresent,
  isDocumentPresentForAnyStakeholder,
  updateTaskStatus,
} from '../../../utils';
import CorporationDocumentUploadSection from './CorporationDocumentUploadSection';
import DirectorDocumentUploadSection from './DirectorDocumentUploadSection';

export const ShareHoldingUploadDocuments = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const { setNotification } = useNotification();

  const corporationStakeholders =
    serviceRequest?.corporation?.corporationHasDirector || [];

  // Common documents for the corporation
  const USCCorpShareholdingDocuments = [
    LegacyDocumentType.UnsignedBoardResolution,
  ];

  // Document types that need to be created for each director
  const USCCorpShareholdingIndividualDirectorDocuments = [
    LegacyDocumentType.UnsignedSharePurchaseAndSaleAgreement,
    LegacyDocumentType.UnsignedShareCertificate,
  ];

  const isTaskCompleted =
    areDocumentsPresent({
      documentTypes: USCCorpShareholdingDocuments,
      serviceRequest,
    }) &&
    USCCorpShareholdingIndividualDirectorDocuments.every((documentType) =>
      isDocumentPresentForAnyStakeholder({
        corporationStakeholders,
        documentType,
      })
    );

  const workflowProcessDrawerVisibility = useToggle();
  const confirmationModalVisibility = useToggle();

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Commenda uploads shareholding documents"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads shareholding documents" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads shareholding documents" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={workflowProcessDrawerVisibility.show}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Upload shareholding documents"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={workflowProcessDrawerVisibility.show}
                variant="primary"
              >
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload shareholding documents" />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={workflowProcessDrawerVisibility.show}
              >
                Continue <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Uploading shareholding documents"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled onClick={() => {}} variant="primary">
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload shareholding documents" />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  const corporationSteps = USCCorpShareholdingDocuments.map((eachDocument) => ({
    heading: documentName(eachDocument),
    key: eachDocument,
    description: '',
    status: isDocumentPresent({
      documentType: eachDocument,
      serviceRequest,
    })
      ? IncorporationRequestStatus.Completed
      : IncorporationRequestStatus.NotStarted,
    children: (
      <CorporationDocumentUploadSection
        serviceRequestTaskId={task.id}
        serviceRequest={serviceRequest}
        mutate={mutate}
        documentType={eachDocument}
        onUploadSuccess={async () => {
          if (task.status === TaskStatus.TO_DO) {
            const res = await updateTask({
              id: task.id,
              data: { status: TaskStatus.IN_PROGRESS },
            });
            if (!res.ok) {
              setNotification({
                show: true,
                valid: false,
                title: 'Error',
                message: 'Failed to update the task status.',
              });
            }
          }
        }}
      />
    ),
  }));

  const directorSteps = USCCorpShareholdingIndividualDirectorDocuments.flatMap(
    (documentType) =>
      corporationStakeholders.map((each) => {
        const isDocAvailable = each?.documents?.some(
          (e) => e?.documentType === documentType
        );
        return {
          heading: `${documentName(documentType)} - ${getPrintableDirectorName({
            directorFirstName: each.director.directorFirstName,
            directorMiddleName: each.director.directorMiddleName,
            directorLastName: each.director.directorLastName,
          })}`,
          key: `${documentType}_${each?.director.id}`,
          description: '',
          status: isDocAvailable
            ? IncorporationRequestStatus.Completed
            : IncorporationRequestStatus.NotStarted,
          children: (
            <DirectorDocumentUploadSection
              serviceRequestTaskId={task.id}
              serviceRequest={serviceRequest}
              mutate={mutate}
              documentType={documentType}
              directorId={each?.director?.id}
              onUploadSuccess={async () => {
                if (task.status === TaskStatus.TO_DO) {
                  const res = await updateTask({
                    id: task.id,
                    data: { status: TaskStatus.IN_PROGRESS },
                  });
                  if (!res.ok) {
                    setNotification({
                      show: true,
                      valid: false,
                      title: 'Error',
                      message: 'Failed to update the task status.',
                    });
                  }
                }
              }}
            />
          ),
        };
      })
  );

  const steps = [...corporationSteps, ...directorSteps];

  const [activeParentStepId, setActiveParentStepId] = useState<string>(
    steps[0].key
  );

  const markTaskAsComplete = async () => {
    await updateTaskStatus({
      task,
      status: TaskStatus.COMPLETED,
      mutate,
    });
    workflowProcessDrawerVisibility.toggle();
    confirmationModalVisibility.hide();
  };

  return (
    <div>
      <WorkflowProcessDrawer
        isShown={workflowProcessDrawerVisibility.visible}
        onClose={workflowProcessDrawerVisibility.hide}
        activeParentStepId={activeParentStepId}
        setActiveParentStepId={setActiveParentStepId}
        hideHeader
        header={
          <div className="w-full flex justify-between mr-4 items-center">
            <h3 className="txt-large-medium text-text-secondary ">
              Upload shareholding documents
            </h3>
            {task.status !== TaskStatus.COMPLETED ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!isTaskCompleted}
                  className="txt-small-medium"
                  onClick={confirmationModalVisibility.show}
                >
                  <Icons.Check className="w-4 h-4" /> Mark task as complete
                </Button>
              </div>
            ) : null}
          </div>
        }
        steps={steps}
      />

      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <ConfirmationModal
        isOpen={confirmationModalVisibility.visible}
        onClose={confirmationModalVisibility.hide}
        onConfirm={() => markTaskAsComplete()}
        title="Mark task as complete."
        message="Are you sure you want to mark this task as complete? This action cannot be undone."
        hideCancelButton
      />
    </div>
  );
};
